// ============================================================
// Date range utilities for analytics period selection
// ============================================================

export type Period = 'today' | '7d' | '30d' | '90d' | '12mo'

export const PERIOD_OPTIONS: Array<{ value: Period; label: string }> = [
    { value: 'today', label: 'Today' },
    { value: '7d', label: '7 days' },
    { value: '30d', label: '30 days' },
    { value: '90d', label: '90 days' },
    { value: '12mo', label: '12 months' },
]

export function periodToDateRange(period: Period): { startDate: string; endDate: string } {
    const end = new Date()
    const start = new Date()

    switch (period) {
        case 'today':
            break
        case '7d':
            start.setDate(start.getDate() - 7)
            break
        case '30d':
            start.setDate(start.getDate() - 30)
            break
        case '90d':
            start.setDate(start.getDate() - 90)
            break
        case '12mo':
            start.setMonth(start.getMonth() - 12)
            break
        default:
            break
    }

    return {
        startDate: start.toISOString().slice(0, 10),
        endDate: end.toISOString().slice(0, 10),
    }
}

/** Today's calendar date (YYYY-MM-DD) in a timezone — v2 report dates are in the project's tz. */
export function todayIn(tz: string | undefined, now = new Date()): string {
    try {
        return new Intl.DateTimeFormat('en-CA', {
            timeZone: tz || undefined,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).format(now)
    } catch {
        return new Intl.DateTimeFormat('en-CA').format(now)
    }
}

/** YYYY-MM-DD +/- days (calendar arithmetic, timezone independent). */
export function shiftDate(date: string, days: number): string {
    const d = new Date(`${date}T00:00:00Z`)
    d.setUTCDate(d.getUTCDate() + days)
    return d.toISOString().slice(0, 10)
}

/** First day of the month of a YYYY-MM-DD date. */
export function monthStart(date: string): string {
    return `${date.slice(0, 7)}-01`
}

/** Inclusive from/to for the v2 report API, ending on `today` (in the project's tz). */
export function periodToRange(period: Period, today: string): { from: string; to: string } {
    const back: Record<Period, number> = { today: 0, '7d': 6, '30d': 29, '90d': 89, '12mo': 364 }
    return { from: shiftDate(today, -(back[period] ?? 6)), to: today }
}

export function granularityForPeriod(period: Period): 'hour' | 'day' | 'week' | 'month' {
    switch (period) {
        case 'today':
            return 'hour'
        case '7d':
        case '30d':
            return 'day'
        case '90d':
            return 'week'
        case '12mo':
            return 'month'
        default:
            return 'day'
    }
}
